import { useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Bot, PanelLeftClose, Plus } from '@/ui/icons'
import { useTrips } from '@/modules/trips'
import { cn } from '@/shared/utils'
import { useRanger } from '../hooks/useRanger'
import { RangerChat } from './RangerChat'

const ICON_BUTTON_CLASS =
  'inline-flex h-8 w-8 items-center justify-center rounded-md text-earth-600 transition-colors hover:bg-earth-100 hover:text-foreground focus:outline-none focus-visible:ring-2 focus-visible:ring-adventure-500'

interface RangerColumnProps {
  tripId?: string
  open: boolean
  onOpen: () => void
  onClose: () => void
}

/**
 * Ranger as a permanent desktop column, between the app navbar and the trips list.
 * The conversation stays mounted while collapsed so it survives closing and reopening,
 * and moving between trips.
 */
export function RangerColumn({ tripId, open, onOpen, onClose }: RangerColumnProps) {
  const navigate = useNavigate()
  const { trips } = useTrips()
  const ranger = useRanger(tripId)
  const { builtTrip, activeTool, messages } = ranger

  // Only a deliberate open takes focus. The automatic one must not pull keyboard focus on page load.
  const [focusRequest, setFocusRequest] = useState(0)
  const handleOpen = () => {
    onOpen()
    setFocusRequest((n) => n + 1)
  }

  // Show the new trip as soon as it exists. The chat stays put for the invite step.
  const navigatedTripIdRef = useRef<string | null>(null)
  useEffect(() => {
    if (!builtTrip || navigatedTripIdRef.current === builtTrip.id) return
    navigatedTripIdRef.current = builtTrip.id
    navigate(`/trips/${builtTrip.id}`)
  }, [builtTrip, navigate])

  const title = activeTool === 'build_trip' ? 'Build a trip' : messages.length > 0 ? 'Ranger' : 'New chat'
  const tripName = tripId ? trips?.find((t) => t.id === tripId)?.name : undefined

  return (
    <aside
      aria-label="Ranger AI Assistant"
      className={cn(
        'hidden h-full flex-shrink-0 overflow-hidden border-r border-earth-200 bg-background transition-[width] duration-200 ease-out motion-reduce:transition-none md:flex',
        open ? 'w-[340px] 2xl:w-[380px]' : 'w-12'
      )}
    >
      {!open && (
        <div className="flex w-12 flex-shrink-0 flex-col items-center py-3">
          <button
            type="button"
            onClick={handleOpen}
            aria-label="Open Ranger AI Assistant"
            title="Open Ranger"
            className={ICON_BUTTON_CLASS}
          >
            <Bot className="h-5 w-5 text-adventure-600" />
          </button>
        </div>
      )}

      <div className={cn('h-full w-[340px] flex-shrink-0 flex-col 2xl:w-[380px]', open ? 'flex' : 'hidden')}>
        <div className="flex h-14 flex-shrink-0 items-center justify-between gap-2 pl-4 pr-2">
          <h2 className="truncate text-sm font-medium text-foreground">{title}</h2>
          <div className="flex shrink-0 items-center gap-0.5">
            {messages.length > 0 && (
              <button
                type="button"
                onClick={ranger.clearMessages}
                aria-label="Start a new chat"
                title="New chat"
                className={ICON_BUTTON_CLASS}
              >
                <Plus className="h-4 w-4" />
              </button>
            )}
            <button
              type="button"
              onClick={onClose}
              aria-label="Collapse Ranger AI Assistant"
              title="Collapse"
              className={ICON_BUTTON_CLASS}
            >
              <PanelLeftClose className="h-4 w-4" />
            </button>
          </div>
        </div>
        <div className="min-h-0 flex-1">
          <RangerChat
            tripId={tripId}
            tripName={tripName}
            messages={messages}
            sendMessage={ranger.sendMessage}
            loading={ranger.loading}
            error={ranger.error}
            clearError={ranger.clearError}
            tools={{ activeTool, tripBuilt: builtTrip !== null, onStart: ranger.startTool, onStop: ranger.stopTool }}
            autoFocus={false}
            focusRequest={focusRequest}
          />
        </div>
      </div>
    </aside>
  )
}
